import type { TextLanguage } from '@/hooks/use-locale'

import { shouldShowNonFsdsNavItems } from '@/utils/domain-branding-resolver'

import { FooterItem, MainNavItem } from '@/types/nav.type'

export const FORUM_NAV_HREF = '/forum'

function isNavItemVisibleForDomain(
  visibility: MainNavItem['domainVisibility'],
  isFsdsDomain: boolean,
  hostname?: string | null
) {
  const resolvedVisibility = visibility ?? 'all'
  if (resolvedVisibility === 'all') return true
  if (resolvedVisibility === 'fsds-only') return isFsdsDomain
  if (resolvedVisibility === 'non-fsds-only') return shouldShowNonFsdsNavItems(isFsdsDomain, hostname)
  return true
}

export function filterMainNavByDomain(
  items: MainNavItem[],
  isFsdsDomain: boolean,
  hostname?: string | null
): MainNavItem[] {
  return items
    .filter(item => isNavItemVisibleForDomain(item.domainVisibility, isFsdsDomain, hostname))
    .map(item => {
      if (!item.items?.length) {
        return item
      }

      return {
        ...item,
        items: filterMainNavByDomain(item.items, isFsdsDomain, hostname) as NonNullable<MainNavItem['items']>,
      }
    })
}

export const getMainNavConfigs = (text: TextLanguage, _lang: string): MainNavItem[] => {
  return [
    {
      width: 596,
      title: 'Học',
      className: 'left-0',
      items: [
        { title: 'Lộ trình', href: '/learning-path', items: [] },
        { title: 'Hall of fame', href: '/hall-of-fame', items: [] },
        { title: 'Blog', href: '/blogs', items: [] },
        { title: 'Webinars', href: '/webinars', items: [] },
        { title: 'Online Tests', href: '/online-tests', items: [], domainVisibility: 'non-fsds-only' },
        { title: 'Bài mẫu', href: '/example-practices', items: [], domainVisibility: 'non-fsds-only' },
        { title: 'Khảo sát', href: '/survey', items: [] },
        { title: 'Reviews', href: '/#reviews', items: [] },
        { title: 'Đội ngũ giảng viên', href: '/#instructors', items: [] },
        { title: 'FAQ', href: '/#faq', items: [] },
      ],
      thumbnail: '/images/home/header/title.jpg',
    },
    {
      title: 'Tuyển dụng',
      className: 'left-10',
      href: '/jobs',
      items: [],
      width: 240,
    },
    {
      title: text.FOOTER.SECTIONS.COMMUNITY,
      href: FORUM_NAV_HREF,
      items: [],
      domainVisibility: 'fsds-only',
    },
  ]
}

export const getFooterLinks = (text: TextLanguage, lang: string): FooterItem[] => {
  const linksEN = [
    {
      title: text.FOOTER.SECTIONS.COURSES,
      items: [
        { title: text.FOOTER.ITEMS.MLOPS_1, href: '/courses/module/mlops-1' },
        { title: text.FOOTER.ITEMS.MLOPS_2, href: '/courses/module/mlops-1' },
        { title: text.FOOTER.ITEMS.MLOPS_3, href: '/courses/module/mlops-3' },
        { title: text.FOOTER.ITEMS.MLOPS_4, href: '/courses/module/mlops-4' },
      ],
    },
    {
      title: text.FOOTER.SECTIONS.COMMUNITY,
      items: [
        { title: 'Blog', href: '/blogs' },
        { title: text.FOOTER.ITEMS.WEBINARS, href: '/webinars' },
        { title: 'Sandbox', href: '/sandbox', inPage: true, items: [] },
        {
          title: text.FOOTER.ITEMS.HALL_OF_FAME,
          href: '/hall-of-fame',
        },
      ],
    },
    {
      title: text.FOOTER.SECTIONS.PLAN,
      items: [
        { title: text.FOOTER.ITEMS.SURVEY, href: '/survey' },
        { title: text.HEADER.INSTRUCTORS, href: '/#instructors', inPage: true, items: [] },
        { title: text.HEADER.FAQ, href: '/#faq', inPage: true, items: [] },
        {
          title: text.HEADER.STUDENT_THOUGHTS,
          href: '/#student-thoughts',
          inPage: true,
          items: [],
        },
      ],
    },
  ]

  const linksVI = [
    {
      title: 'Khoá MLOps',
      items: [
        {
          title: 'Fundamentals of Software Engineering for Data',
          href: '/courses/module/fundamentals-of-software-engineering?c=mlops',
        },
        {
          title: 'MLOps 1',
          href: '/courses/module/mlops-1?c=mlops',
        },
        {
          title: 'MLOps 2',
          href: '/courses/module/mlops-2?c=mlops',
        },
        {
          title: 'MLOps 3',
          href: '/courses/module/mlops-3?c=mlops',
        },
        {
          title: 'MLOps 4',
          href: '/courses/module/mlops-4?c=mlops',
        },
        {
          title: 'LLMOps & AgentOps',
          href: '/courses/module/llmops-agentops?c=mlops',
        },
      ],
    },
    {
      title: 'Khoá học AI & Data Engineering',
      items: [
        {
          title: 'Fundamentals of Software Engineering for Data',
          href: '/courses/module/fundamentals-of-software-engineering?c=aide',
          items: [],
        },
        {
          title: 'AIDE 1 (MLOps & DataOps)',
          href: '/courses/module/mlops-dataops?c=aide',
          items: [],
        },
        {
          title: 'AIDE 2 (Data Engineering)',
          href: '/courses/module/data-engineering?c=aide',
          items: [],
        },
        {
          title: 'AIDE 3 (ML Engineering)',
          href: '/courses/module/machine-learning-engineering?c=aide',
          items: [],
        },
      ],
    },
    {
      title: 'Khoá học Data Science & Analytics',
      items: [
        {
          title: 'Fundamentals of Software Engineering for Data',
          href: '/courses/module/fundamentals-of-software-engineering?c=dsaa',
        },
        {
          title: 'Machine Learning',
          href: '/courses/module/machine-learning?c=dsaa',
        },
        {
          title: 'Business Intelligence',
          href: '/courses/module/business-intelligence?c=dsaa',
        },
        {
          title: 'Deep Learning',
          href: '/courses/module/deep-learning?c=dsaa',
        },
      ],
    },

    {
      title: 'Khoá học Advanced Data Science',
      items: [
        {
          title: 'Advanced Computer Vision',
          href: '/courses/module/computer-vision?c=igds',
        },
        {
          title: 'Advanced NLP',
          href: '/courses/module/nlp?c=igds',
        },
        {
          title: 'LLM',
          href: '/courses/module/llm?c=igds',
        },
        {
          title: 'Speech',
          href: '/courses/module/speech?c=igds',
        },
        {
          title: 'Recommendation System',
          href: '/courses/module/rec-sys?c=igds',
        },
      ],
    },

    {
      title: text.FOOTER.SECTIONS.COMMUNITY,
      items: [
        { title: 'Blog', href: '/blogs' },
        { title: text.FOOTER.ITEMS.WEBINARS, href: '/webinars' },
        { title: 'Sandbox', href: '/sandbox', inPage: true, items: [] },
        {
          title: text.FOOTER.ITEMS.HALL_OF_FAME,
          href: '/hall-of-fame',
        },
        { title: 'Meme', href: '/meme', items: [], icon: 'meme' },
      ],
    },
    {
      title: text.FOOTER.SECTIONS.PLAN,
      items: [
        { title: text.FOOTER.ITEMS.SURVEY, href: '/survey' },
        {
          title: text.HEADER.STUDENT_THOUGHTS,
          href: '/#student-thoughts',
          inPage: true,
          items: [],
        },
        { title: text.HEADER.INSTRUCTORS, href: '/#instructors', inPage: true, items: [] },
        { title: text.HEADER.FAQ, href: '/#faq', inPage: true, items: [] },
      ],
    },
  ]

  return lang === 'en' ? linksEN : linksVI
}
